/**
 * Gift Card Service
 * Manages gift card purchases, redemptions, and balance tracking
 */

import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { SoldVia, RedemptionBreakdown } from '../shared/giftCard';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface GiftCardTemplate {
  id: string;
  name: string;
  imageUrl: string;
  description: string;
  season?: 'default' | 'holiday' | 'valentines' | 'mothers-day' | 'fathers-day' | 'birthday';
  active: boolean;
  createdAt: Date;
}

export interface GiftCard {
  id: string;
  code: string; // Unique gift card code (e.g., "SLIK-XXXX-XXXX")
  templateId: string;

  // Value & Balance
  initialValue: number;
  currentBalance: number;

  // Ownership
  purchasedBy?: string; // User ID of purchaser
  purchasedByName?: string;
  recipientName?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  personalMessage?: string;

  // Delivery
  deliveryMethod: 'email' | 'print' | 'in-person';
  deliveryDate?: Date;
  emailSent?: boolean;

  // Status & Tracking
  status: 'pending' | 'active' | 'redeemed' | 'expired' | 'void';
  issuedDate: Date;
  expirationDate?: Date;

  // Metadata
  createdAt: Date;
  createdBy: string; // User ID or 'system'
  updatedAt: Date;

  // Transaction history reference
  transactionCount: number;

  // Attribution (2026-08-15) — where the card was sold + which barber sold it, so
  // reporting can split gift-card sales per surface (pos/portal/public) and per barber.
  soldVia?: SoldVia;
  soldByBarberId?: string;

  // Linking (2026-11-01) — a card saved to a client account shows as a payment method on their appt card.
  linkedClientId?: string;
  // Provenance for imported cards (Fresha) so reporting never counts them as new revenue.
  source?: string;
  freshaSaleNumber?: string;
}

export interface GiftCardTransaction {
  id: string;
  giftCardId: string;
  giftCardCode: string;

  type: 'purchase' | 'redemption' | 'refund' | 'adjustment';
  amount: number; // Positive for adding, negative for deducting

  // Transaction details
  description: string;
  relatedAppointmentId?: string;
  relatedInvoiceId?: string;

  // Balance tracking
  balanceBefore: number;
  balanceAfter: number;

  // Metadata
  processedBy: string; // User ID or staff ID
  processedByName?: string;
  timestamp: Date;
  notes?: string;

  // Redemption tracing (2026-08-15) — which barber's work it paid + how the drawn dollars
  // split across service/product/tip, so every redemption is fully traceable in the hub.
  barberId?: string;
  redemptionBreakdown?: RedemptionBreakdown;
}

// ============================================================================
// GIFT CARD SERVICE
// ============================================================================

class GiftCardService {
  private static instance: GiftCardService;

  private constructor() {}

  static getInstance(): GiftCardService {
    if (!GiftCardService.instance) {
      GiftCardService.instance = new GiftCardService();
    }
    return GiftCardService.instance;
  }

  /**
   * Generate a unique gift card code
   */
  private generateGiftCardCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid confusing chars (0, O, I, 1)
    const segments = 3;
    const segmentLength = 4;

    const code = Array.from({ length: segments }, () => {
      return Array.from({ length: segmentLength }, () =>
        chars.charAt(Math.floor(Math.random() * chars.length))
      ).join('');
    }).join('-');

    return `SLIK-${code}`;
  }

  /**
   * Check if a gift card code is unique
   */
  private async isCodeUnique(code: string): Promise<boolean> {
    const q = query(
      collection(db, 'giftCards'),
      where('code', '==', code),
      limit(1)
    );
    const snapshot = await getDocs(q);
    return snapshot.empty;
  }

  /**
   * Generate a unique gift card code (ensures uniqueness)
   */
  async generateUniqueCode(): Promise<string> {
    let code: string;
    let attempts = 0;
    const maxAttempts = 10;

    do {
      code = this.generateGiftCardCode();
      attempts++;

      if (attempts >= maxAttempts) {
        throw new Error('Failed to generate unique gift card code after multiple attempts');
      }
    } while (!(await this.isCodeUnique(code)));

    return code;
  }

  /**
   * Create a new gift card
   */
  async createGiftCard(params: {
    templateId: string;
    initialValue: number;
    purchasedBy?: string;
    purchasedByName?: string;
    recipientName?: string;
    recipientEmail?: string;
    recipientPhone?: string;
    personalMessage?: string;
    deliveryMethod: 'email' | 'print' | 'in-person';
    deliveryDate?: Date;
    expirationMonths?: number; // Number of months until expiration (default: 12)
    createdBy: string;
    soldVia?: SoldVia;
    soldByBarberId?: string;
    code?: string; // optional PHYSICAL card number to associate; else one is generated
  }): Promise<GiftCard> {
    try {
      // Custom code (2026-08-16): staff can associate a PHYSICAL card's PRINTED number so scanning or
      // typing that number later resolves this card. If supplied, enforce uniqueness; else generate.
      let code: string;
      if (params.code && params.code.trim()) {
        code = params.code.trim().toUpperCase();
        const existing = await this.getGiftCardByCode(code);
        if (existing) throw new Error(`Gift card number "${code}" is already in use`);
      } else {
        code = await this.generateUniqueCode();
      }

      // Calculate expiration date (default 12 months)
      const expirationMonths = params.expirationMonths || 12;
      const expirationDate = new Date();
      expirationDate.setMonth(expirationDate.getMonth() + expirationMonths);

      // Create gift card object - filter out undefined values for Firestore
      const giftCardData: any = {
        code,
        templateId: params.templateId,
        initialValue: params.initialValue,
        currentBalance: params.initialValue,
        deliveryMethod: params.deliveryMethod,
        emailSent: false,
        status: 'active',
        issuedDate: new Date(),
        expirationDate,
        createdAt: new Date(),
        createdBy: params.createdBy,
        updatedAt: new Date(),
        transactionCount: 0
      };

      // Only add optional fields if they have values (Firestore doesn't accept undefined)
      if (params.purchasedBy) giftCardData.purchasedBy = params.purchasedBy;
      if (params.purchasedByName) giftCardData.purchasedByName = params.purchasedByName;
      if (params.recipientName) giftCardData.recipientName = params.recipientName;
      if (params.recipientEmail) giftCardData.recipientEmail = params.recipientEmail;
      if (params.recipientPhone) giftCardData.recipientPhone = params.recipientPhone;
      if (params.personalMessage) giftCardData.personalMessage = params.personalMessage;
      if (params.deliveryDate) giftCardData.deliveryDate = params.deliveryDate;
      // Attribution (2026-08-15): where sold + which barber — undefined-safe for Firestore.
      if (params.soldVia) giftCardData.soldVia = params.soldVia;
      if (params.soldByBarberId) giftCardData.soldByBarberId = params.soldByBarberId;

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'giftCards'), giftCardData);

      const giftCard: GiftCard = {
        id: docRef.id,
        ...giftCardData
      };

      // Create initial purchase transaction
      await this.createTransaction({
        giftCardId: docRef.id,
        giftCardCode: code,
        type: 'purchase',
        amount: params.initialValue,
        description: `Gift card purchased - Initial value: $${params.initialValue}`,
        balanceBefore: 0,
        balanceAfter: params.initialValue,
        processedBy: params.createdBy,
        processedByName: params.purchasedByName,
        notes: params.personalMessage
      });

      console.log('✅ Gift card created:', code);

      return giftCard;
    } catch (error) {
      console.error('❌ Error creating gift card:', error);
      throw error;
    }
  }

  /**
   * IMPORT a gift card from an external system (Fresha CSV). (Jose 2026-11-01)
   * Upserts by code — carries the BALANCE/liability over WITHOUT booking a 'purchase' transaction.
   * Those cards were already earned as revenue in Fresha; booking a purchase here would DOUBLE-COUNT.
   * Tagged `source:'fresha-import'` so reporting can exclude them from "Gift Cards Sold". Idempotent:
   * re-running the same CSV updates balance/status instead of creating duplicates.
   */
  async importGiftCard(d: {
    code: string;
    initialValue: number;
    currentBalance: number;
    status: GiftCard['status'];
    issuedDate: Date;
    expirationDate?: Date | null;
    purchasedByName?: string;
    recipientName?: string;
    freshaSaleNumber?: string;
    createdBy: string;
  }): Promise<'created' | 'updated'> {
    const code = d.code.trim().toUpperCase();
    const data: any = {
      code,
      initialValue: d.initialValue,
      currentBalance: d.currentBalance,
      status: d.status,
      issuedDate: d.issuedDate,
      deliveryMethod: 'in-person',
      source: 'fresha-import',          // marks it so reporting never counts it as new revenue
      updatedAt: new Date(),
    };
    if (d.expirationDate) data.expirationDate = d.expirationDate;
    if (d.purchasedByName) data.purchasedByName = d.purchasedByName;
    if (d.recipientName) data.recipientName = d.recipientName;
    if (d.freshaSaleNumber) data.freshaSaleNumber = d.freshaSaleNumber;

    const existing = await this.getGiftCardByCode(code);
    if (existing) {
      await updateDoc(doc(db, 'giftCards', existing.id), data);
      return 'updated';
    }
    data.templateId = 'fresha-import';
    data.emailSent = true;
    data.transactionCount = 0;
    data.createdAt = new Date();
    data.createdBy = d.createdBy;
    await addDoc(collection(db, 'giftCards'), data);
    return 'created';
  }

  /**
   * Link a gift card to a client account so it shows as a payment method on their appointment card.
   * (Jose 2026-11-01) One-tap "add this card to this account" at checkout stamps linkedClientId.
   */
  async linkToClient(giftCardId: string, clientId: string): Promise<void> {
    await updateDoc(doc(db, 'giftCards', giftCardId), { linkedClientId: clientId, updatedAt: new Date() });
  }

  /**
   * The ACTIVE gift cards linked to a client (for the appointment-card payment method).
   * Query by linkedClientId only (single-field, no composite index) + filter active + balance client-side.
   */
  async getActiveGiftCardsForClient(clientId: string): Promise<GiftCard[]> {
    if (!clientId) return [];
    try {
      const snap = await getDocs(query(collection(db, 'giftCards'), where('linkedClientId', '==', clientId)));
      return snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }) as GiftCard)
        .filter((g) => g.status === 'active' && (g.currentBalance || 0) > 0)
        .sort((a, b) => (b.currentBalance || 0) - (a.currentBalance || 0));
    } catch (e) {
      console.error('[giftCard] getActiveGiftCardsForClient failed:', e);
      return [];
    }
  }

  /**
   * Create a transaction record
   */
  private async createTransaction(params: {
    giftCardId: string;
    giftCardCode: string;
    type: 'purchase' | 'redemption' | 'refund' | 'adjustment';
    amount: number;
    description: string;
    relatedAppointmentId?: string;
    relatedInvoiceId?: string;
    balanceBefore: number;
    balanceAfter: number;
    processedBy: string;
    processedByName?: string;
    notes?: string;
    barberId?: string;
    redemptionBreakdown?: RedemptionBreakdown;
  }, batch?: ReturnType<typeof writeBatch>): Promise<void> {
    // Create transaction object - filter out undefined values for Firestore
    const transactionData: any = {
      giftCardId: params.giftCardId,
      giftCardCode: params.giftCardCode,
      type: params.type,
      amount: params.amount,
      description: params.description,
      balanceBefore: params.balanceBefore,
      balanceAfter: params.balanceAfter,
      processedBy: params.processedBy,
      timestamp: new Date()
    };

    // Only add optional fields if they have values (Firestore doesn't accept undefined)
    if (params.relatedAppointmentId) transactionData.relatedAppointmentId = params.relatedAppointmentId;
    if (params.relatedInvoiceId) transactionData.relatedInvoiceId = params.relatedInvoiceId;
    if (params.processedByName) transactionData.processedByName = params.processedByName;
    if (params.notes) transactionData.notes = params.notes;
    // Redemption tracing (2026-08-15): barber + service/product/tip split, undefined-safe.
    if (params.barberId) transactionData.barberId = params.barberId;
    if (params.redemptionBreakdown) transactionData.redemptionBreakdown = params.redemptionBreakdown;

    // H28 FIX (Jose 2026-10-29 audit): when a `batch` is supplied, enlist the ledger write into it so a
    // caller (e.g. redeemGiftCard) can commit the balance draw-down AND this transaction atomically —
    // no more "card debited but no ledger entry" if the second write fails. Standalone callers (issue/
    // reload) keep the simple addDoc path.
    if (batch) {
      batch.set(doc(collection(db, 'giftCardTransactions')), transactionData);
    } else {
      await addDoc(collection(db, 'giftCardTransactions'), transactionData);
    }
  }

  /**
   * Get gift card by code
   */
  async getGiftCardByCode(code: string): Promise<GiftCard | null> {
    try {
      const q = query(
        collection(db, 'giftCards'),
        where('code', '==', code.toUpperCase()),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        issuedDate: doc.data().issuedDate?.toDate() || new Date(),
        expirationDate: doc.data().expirationDate?.toDate(),
        deliveryDate: doc.data().deliveryDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      } as GiftCard;
    } catch (error) {
      console.error('❌ Error fetching gift card:', error);
      throw error;
    }
  }

  /**
   * Redeem gift card (apply to payment)
   */
  async redeemGiftCard(params: {
    code: string;
    amount: number;
    relatedAppointmentId?: string;
    relatedInvoiceId?: string;
    processedBy: string;
    processedByName?: string;
    barberId?: string;
    breakdown?: RedemptionBreakdown;
  }): Promise<{ success: boolean; newBalance: number; message: string }> {
    try {
      const giftCard = await this.getGiftCardByCode(params.code);

      if (!giftCard) {
        return {
          success: false,
          newBalance: 0,
          message: 'Gift card not found'
        };
      }

      // Validate gift card status
      if (giftCard.status !== 'active') {
        return {
          success: false,
          newBalance: giftCard.currentBalance,
          message: `Gift card is ${giftCard.status}`
        };
      }

      // Check expiration
      if (giftCard.expirationDate && new Date() > giftCard.expirationDate) {
        // Update status to expired
        await updateDoc(doc(db, 'giftCards', giftCard.id), {
          status: 'expired',
          updatedAt: new Date()
        });

        return {
          success: false,
          newBalance: giftCard.currentBalance,
          message: 'Gift card has expired'
        };
      }

      // Check balance
      if (giftCard.currentBalance < params.amount) {
        return {
          success: false,
          newBalance: giftCard.currentBalance,
          message: `Insufficient balance. Available: $${giftCard.currentBalance.toFixed(2)}`
        };
      }

      // Calculate new balance
      const newBalance = giftCard.currentBalance - params.amount;
      const newStatus = newBalance === 0 ? 'redeemed' : 'active';

      // H28 FIX (Jose 2026-10-29 audit): commit the balance draw-down AND the redemption ledger entry
      // in ONE atomic batch. Previously these were two sequential writes — if the ledger addDoc failed
      // after the balance updateDoc succeeded, the card was debited with NO audit trail while checkout
      // still reported success (books silently incomplete). A batch lands both or neither.
      const batch = writeBatch(db);
      batch.update(doc(db, 'giftCards', giftCard.id), {
        currentBalance: newBalance,
        status: newStatus,
        updatedAt: new Date(),
        transactionCount: giftCard.transactionCount + 1
      });
      await this.createTransaction({
        giftCardId: giftCard.id,
        giftCardCode: giftCard.code,
        type: 'redemption',
        amount: -params.amount,
        description: `Redeemed $${params.amount.toFixed(2)}`,
        relatedAppointmentId: params.relatedAppointmentId,
        relatedInvoiceId: params.relatedInvoiceId,
        balanceBefore: giftCard.currentBalance,
        balanceAfter: newBalance,
        processedBy: params.processedBy,
        processedByName: params.processedByName,
        barberId: params.barberId,
        redemptionBreakdown: params.breakdown
      }, batch);
      await batch.commit();

      console.log(`✅ Gift card redeemed: ${params.code} - $${params.amount}`);

      return {
        success: true,
        newBalance,
        message: `Successfully applied $${params.amount.toFixed(2)}. Remaining balance: $${newBalance.toFixed(2)}`
      };
    } catch (error) {
      console.error('❌ Error redeeming gift card:', error);
      throw error;
    }
  }

  /**
   * All gift-card transactions whose timestamp falls in [from, to] — for reporting
   * (redeemed-in-range + per-card tracing). (2026-08-15)
   */
  async getTransactionsInRange(from: Date, to: Date): Promise<GiftCardTransaction[]> {
    try {
      const q = query(
        collection(db, 'giftCardTransactions'),
        where('timestamp', '>=', from),
        where('timestamp', '<=', to),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        timestamp: (d.data() as any).timestamp?.toDate?.() || new Date(),
      })) as GiftCardTransaction[];
    } catch (error) {
      console.error('❌ Error fetching gift-card transactions in range:', error);
      return [];
    }
  }

  /**
   * Get gift card transactions
   */
  async getGiftCardTransactions(giftCardId: string): Promise<GiftCardTransaction[]> {
    try {
      const q = query(
        collection(db, 'giftCardTransactions'),
        where('giftCardId', '==', giftCardId)
      );

      const snapshot = await getDocs(q);

      // Sort in JavaScript instead of Firestore to avoid needing a composite index
      const transactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      })) as GiftCardTransaction[];

      // Sort by timestamp descending (most recent first)
      return transactions.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    } catch (error) {
      console.error('❌ Error fetching transactions:', error);
      return [];
    }
  }

  /**
   * Get all gift cards for a user
   */
  async getUserGiftCards(userId: string): Promise<GiftCard[]> {
    try {
      const q = query(
        collection(db, 'giftCards'),
        where('purchasedBy', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        issuedDate: doc.data().issuedDate?.toDate() || new Date(),
        expirationDate: doc.data().expirationDate?.toDate(),
        deliveryDate: doc.data().deliveryDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as GiftCard[];
    } catch (error) {
      console.error('❌ Error fetching user gift cards:', error);
      return [];
    }
  }

  /**
   * Get gift cards received by a user (by email)
   */
  async getReceivedGiftCards(email: string): Promise<GiftCard[]> {
    try {
      const q = query(
        collection(db, 'giftCards'),
        where('recipientEmail', '==', email),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        issuedDate: doc.data().issuedDate?.toDate() || new Date(),
        expirationDate: doc.data().expirationDate?.toDate(),
        deliveryDate: doc.data().deliveryDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as GiftCard[];
    } catch (error) {
      console.error('❌ Error fetching received gift cards:', error);
      return [];
    }
  }

  /**
   * Get all gift cards (Staff only)
   */
  async getAllGiftCards(): Promise<GiftCard[]> {
    try {
      const q = query(
        collection(db, 'giftCards'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        issuedDate: doc.data().issuedDate?.toDate() || new Date(),
        expirationDate: doc.data().expirationDate?.toDate(),
        deliveryDate: doc.data().deliveryDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as GiftCard[];
    } catch (error) {
      console.error('❌ Error fetching all gift cards:', error);
      return [];
    }
  }
}

export const giftCardService = GiftCardService.getInstance();
